import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import type { CalendarPost } from '../types';
import { EMPTY_DAY } from './calendarDayModel';
import type { CalendarGridProps } from './calendarGridTypes';
import { AgendaRow } from './AgendaRow';
import { ReleaseBand } from './ReleaseBand';
import { RolloutStepMark } from './RolloutStepMark';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The List view (design c1, the default below 700 px): the "Needs you" group first (it holds the
 * failed posts too), then each day with a post, a release or a rollout step. The whole row is the
 * target. There is no drag in the List: a post moves through "Change the time" in its sheet.
 */
const AgendaListComponent: React.FC<CalendarGridProps> = ({ days, contentByDay, songs, onOpenPost }) => {
  const intl = useIntl();
  const attention = useMemo<CalendarPost[]>(
    () =>
      days.flatMap((day) =>
        (contentByDay.get(day.key) ?? EMPTY_DAY).posts.filter(
          (post) => post.status === 'awaitingManualPost' || post.status === 'failed'
        )
      ),
    [contentByDay, days]
  );
  const filled = useMemo(
    () =>
      days
        .map((day) => ({ day, content: contentByDay.get(day.key) ?? EMPTY_DAY }))
        .filter(({ content }) => content.posts.length + content.releases.length + content.steps.length > 0),
    [contentByDay, days]
  );
  const songOf = (post: CalendarPost) => (post.catalogItemUuid === null ? undefined : songs.get(post.catalogItemUuid));

  return (
    <div className={styles.agenda} data-testid="calendar-agenda">
      {attention.length > 0 ? (
        <section className={styles.agendaGroup}>
          <h3 className={styles.agendaHead}>
            {intl.formatMessage({ id: 'app.sml.promote.calendar.list.needsYou', defaultMessage: 'Needs you' })}
          </h3>
          {attention.map((post) => (
            <AgendaRow key={post.uuid} post={post} song={songOf(post)} withDate onOpen={onOpenPost} />
          ))}
        </section>
      ) : null}
      {filled.map(({ day, content }) => (
        <section key={day.key} className={styles.agendaGroup}>
          <h3 className={styles.agendaHead}>
            {day.isToday
              ? intl.formatMessage({ id: 'app.sml.promote.calendar.list.today', defaultMessage: 'Today' })
              : intl.formatDate(day.start, { weekday: 'long', day: 'numeric', month: 'long' })}
          </h3>
          {content.releases.map((song) => (
            <ReleaseBand key={song.catalogItemUuid} song={song} />
          ))}
          {content.steps.map((step) => (
            <RolloutStepMark key={step.rolloutKey} step={step} song={songs.get(step.catalogItemUuid)} />
          ))}
          {content.posts.map((post) => (
            <AgendaRow key={post.uuid} post={post} song={songOf(post)} onOpen={onOpenPost} />
          ))}
        </section>
      ))}
    </div>
  );
};

export const AgendaList = TypedMemo(AgendaListComponent);
